import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  SelectField,
  Status,
  TableData,
  Tabs,
  Toggle,
} from '@/components';
import { showCaughtErrorToast } from '@/components/toast/globalToast';
import { Column } from '@/types/common';
import { EscalationStatus } from '@/types/rental';
import {
  getRentalTracking,
  getTrackingAreaBoundaries,
  MAX_AREA_BOUNDARIES,
  RentalTrackingItem,
  TrackingAreaBoundary,
} from '@/services/api/rentTracking';
import {
  getVehicleTrail,
  VehicleTrailPoint,
} from '@/services/api/vehicleTelemetry';
import { formatRupiah } from '@/utils/format';
import {
  AreaLevel,
  countChosenFilters,
  DEFAULT_POLL_INTERVAL_MINUTES,
  EMPTY_TRACKING_FILTERS,
  filterTrackingItems,
  fixFreshness,
  formatFixAge,
  freshnessToneClass,
  groupTrackingByArea,
  hasActiveTrackingFilters,
  POLL_INTERVAL_OPTIONS,
  sortTrackingItems,
  summariseTracking,
  tallyByRung,
  trackingFilterOptions,
  TrackingFilters,
  TRACKING_NO_VALUE,
  trackingSearchSuggestions,
  TrailRange,
  UNRESOLVED_AREA_LABEL,
} from './rentTrackingDisplay';
import { AreaBreakdownPanel } from './components/AreaBreakdownPanel';
import { TrackingFilterBar } from './components/TrackingFilterBar';
import { TrackingMapLegend } from './components/TrackingMapLegend';
import { TrackingSummaryBar } from './components/TrackingSummaryBar';
import { UnitTrailPanel } from './components/UnitTrailPanel';

// mapbox-gl is ~1.8 MB; only this screen needs it, so it loads on demand.
const TrackingMap = React.lazy(() => import('./components/TrackingMap'));

// The rungs where somebody has to go to the unit — the API's own default.
const DEFAULT_RUNGS: EscalationStatus[] = [
  EscalationStatus.SHUTDOWN_PENDING,
  EscalationStatus.SHUTDOWN,
  EscalationStatus.REPO_REQUESTED,
];

type TabKey = 'map' | 'table';
const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'map', label: 'Peta', icon: 'ri-map-2-line' },
  { key: 'table', label: 'Tabel', icon: 'ri-table-line' },
];

// The floating panel covers the right of the map, so the fit has to keep that
// much clear or the rightmost pins land underneath it. 420px panel + insets.
const MAP_PANEL_PADDING = { right: 452, top: 24, bottom: 24, left: 24 };

const sameRungs = (a: EscalationStatus[], b: EscalationStatus[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const RentTrackingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('map');
  const [items, setItems] = useState<RentalTrackingItem[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rungs, setRungs] = useState<EscalationStatus[]>(DEFAULT_RUNGS);
  const [filters, setFilters] = useState<TrackingFilters>(
    EMPTY_TRACKING_FILTERS
  );
  const [areaLevel, setAreaLevel] = useState<AreaLevel>('city');
  const [showLabels, setShowLabels] = useState(false);
  const [showAreaOutlines, setShowAreaOutlines] = useState(true);
  const [areaBoundaries, setAreaBoundaries] = useState<TrackingAreaBoundary[]>(
    []
  );
  // Outlines are immutable reference data, so one fetch per area per session.
  const boundaryCache = useRef(new Map<string, TrackingAreaBoundary>());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [intervalMinutes, setIntervalMinutes] = useState(
    DEFAULT_POLL_INTERVAL_MINUTES
  );
  const [selectedID, setSelectedID] = useState<string | null>(null);

  // Movement history of the selected unit; the cursor only means anything
  // while a trail is loaded, so the two are cleared together.
  const [trail, setTrail] = useState<VehicleTrailPoint[] | null>(null);
  const [cursorMs, setCursorMs] = useState<number | null>(null);
  const [isLoadingTrail, setIsLoadingTrail] = useState(false);
  const clearTrail = useCallback(() => {
    setTrail(null);
    setCursorMs(null);
  }, []);

  const load = useCallback(
    async (background = false) => {
      if (background) setIsRefreshing(true);
      else setIsLoading(true);
      try {
        const result = await getRentalTracking({
          escalationStatus: rungs.length ? rungs : undefined,
          includeRepossessionCases: true,
        });
        setItems(sortTrackingItems(result.items));
        setAsOf(result.asOf);
      } catch (error) {
        showCaughtErrorToast(error, 'Gagal memuat tracking');
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [rungs]
  );

  useEffect(() => {
    load();
  }, [load]);

  // A poll can drop the selected unit (paid up, case closed): close its panel
  // AND its trail, or the line keeps drawing for a unit that is gone. Only the
  // server list counts here — filtering one out must not shut the panel ops
  // deliberately opened.
  useEffect(() => {
    if (selectedID && !items.some((i) => i.rentalID === selectedID)) {
      setSelectedID(null);
      clearTrail();
    }
  }, [items, selectedID, clearTrail]);

  // The poll. Telemetry itself lands every few minutes (Alva pushes, Electrum
  // is pulled in rotation), so anything faster than this only re-reads the
  // same snapshot.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const handle = window.setInterval(
      () => load(true),
      intervalMinutes * 60_000
    );
    return () => window.clearInterval(handle);
  }, [autoRefresh, intervalMinutes, load]);

  const visibleItems = useMemo(
    () => filterTrackingItems(items, filters),
    [items, filters]
  );
  const filterOptions = useMemo(() => trackingFilterOptions(items), [items]);
  const selected = useMemo(
    () => items.find((i) => i.rentalID === selectedID) ?? null,
    [items, selectedID]
  );
  const summary = useMemo(
    () => summariseTracking(visibleItems),
    [visibleItems]
  );
  // Follows the search and the other filters so its totals agree with the
  // count beside the search box, but NOT the area filter itself — a breakdown
  // that shrinks to the one area you just clicked cannot be clicked back.
  const areaGroups = useMemo(
    () =>
      groupTrackingByArea(
        filterTrackingItems(items, filters, { ignoreAreas: true }),
        areaLevel
      ),
    [items, filters, areaLevel]
  );
  const selectedAreaCodes =
    areaLevel === 'city' ? filters.cities : filters.districts;
  // Off the unfiltered rows, so picking one rung does not erase the others
  // from the legend and strand the reader there — same rule as the areas.
  const rungTallies = useMemo(() => tallyByRung(items), [items]);

  const toggleRung = useCallback((rung: EscalationStatus) => {
    setFilters((previous) => ({
      ...previous,
      rungs: previous.rungs.includes(rung)
        ? previous.rungs.filter((value) => value !== rung)
        : [...previous.rungs, rung],
    }));
  }, []);

  const clearRungFilter = useCallback(() => {
    setFilters((previous) => ({ ...previous, rungs: [] }));
  }, []);

  const toggleArea = useCallback(
    (code: string) => {
      const key = areaLevel === 'city' ? 'cities' : 'districts';
      setFilters((previous) => ({
        ...previous,
        [key]: previous[key].includes(code)
          ? previous[key].filter((value) => value !== code)
          : [...previous[key], code],
      }));
    },
    [areaLevel]
  );

  const select = useCallback(
    (rentalID: string | null) => {
      setSelectedID(rentalID);
      clearTrail();
    },
    [clearTrail]
  );

  const changeRungs = useCallback((next: EscalationStatus[]) => {
    // Re-applying the same rungs must not refetch and reset the poll.
    setRungs((previous) => (sameRungs(next, previous) ? previous : next));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_TRACKING_FILTERS);
    setRungs((previous) =>
      sameRungs(DEFAULT_RUNGS, previous) ? previous : DEFAULT_RUNGS
    );
  }, []);

  const loadTrail = async (range: TrailRange) => {
    if (!selected?.vehicleID) return;
    setIsLoadingTrail(true);
    try {
      const result = await getVehicleTrail(
        selected.vehicleID,
        range.from,
        range.to
      );
      setTrail(result.points);
      setCursorMs(null);
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal memuat jejak');
    } finally {
      setIsLoadingTrail(false);
    }
  };

  const columns = useMemo<Column<RentalTrackingItem>[]>(
    () => [
      {
        key: 'platNomor',
        title: 'Unit',
        render: (_value, record) => (
          <div>
            <p className="font-medium text-gray-900">
              {record.platNomor ?? '-'}
            </p>
            <p className="text-[12px] text-gray-500">
              {[record.oemName, record.modelName].filter(Boolean).join(' ') ||
                '-'}
            </p>
          </div>
        ),
      },
      {
        key: 'driverName',
        title: 'Mitra',
        render: (_value, record) => (
          <div>
            <p className="text-gray-900">{record.driverName ?? '-'}</p>
            <p className="text-[12px] text-gray-500">
              {record.driverCode ?? record.driverID} · {record.rentalCode}
            </p>
          </div>
        ),
      },
      {
        key: 'escalationStatus',
        title: 'Status',
        render: (_value, record) => (
          <div className="flex flex-col items-start gap-1">
            {record.escalationStatus === EscalationStatus.NORMAL ? (
              <span className="text-gray-500">-</span>
            ) : (
              <Status
                value={record.escalationStatus}
                type="escalationStatus"
                size="small"
              />
            )}
            {record.repossession && (
              <span className="text-[12px] text-primary font-medium">
                {record.repossession.code} · {record.repossession.status}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'arrears',
        title: 'Tunggakan',
        align: 'right',
        render: (_value, record) => (
          <div className="text-right">
            <p className="text-gray-900">
              {formatRupiah(record.arrears.amount)}
            </p>
            <p className="text-[12px] text-gray-500">
              {record.arrears.days} hari
            </p>
          </div>
        ),
      },
      {
        key: 'position',
        title: 'Posisi Terakhir',
        render: (_value, record) => {
          const freshness = fixFreshness(record);
          return (
            <div className="flex flex-col gap-1">
              <span
                className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium ${freshnessToneClass(
                  freshness
                )}`}
              >
                <i className="ri-map-pin-2-line"></i>
                {freshness === 'none'
                  ? 'Tidak ada fix'
                  : formatFixAge(record.position!.ageMinutes)}
              </span>
              {record.position && (
                <span className="text-[12px] text-gray-500">
                  {record.position.ignition === null
                    ? ''
                    : record.position.ignition
                      ? 'Menyala'
                      : 'Mati'}
                  {record.position.speed !== null
                    ? ` · ${Math.round(record.position.speed)} km/j`
                    : ''}
                </span>
              )}
              <span className="text-[12px] text-gray-500">
                {[record.zone?.districtName, record.zone?.cityName]
                  .filter(Boolean)
                  .join(', ') || UNRESOLVED_AREA_LABEL}
              </span>
            </div>
          );
        },
      },
      {
        key: 'hubName',
        title: 'Hub',
        render: (value) => value ?? '-',
      },
    ],
    []
  );

  // Every area the fleet actually sits in, not every area in the country: all
  // 7,284 kecamatan at full fidelity is over ten megabytes, and a zone with no
  // unit in it says nothing on a tracking map.
  const outlineCodes = useMemo(() => {
    const options =
      areaLevel === 'city' ? filterOptions.cities : filterOptions.districts;
    return options
      .map((area) => area.code)
      .filter((code) => code !== TRACKING_NO_VALUE)
      .slice(0, MAX_AREA_BOUNDARIES);
  }, [areaLevel, filterOptions]);

  useEffect(() => {
    if (!showAreaOutlines || outlineCodes.length === 0) {
      setAreaBoundaries([]);
      return undefined;
    }

    const level = areaLevel === 'city' ? 'CITY' : 'DISTRICT';
    const missing = outlineCodes.filter(
      (code) => !boundaryCache.current.has(`${level}:${code}`)
    );

    let current = true;
    const paint = () => {
      if (!current) return;
      setAreaBoundaries(
        outlineCodes
          .map((code) => boundaryCache.current.get(`${level}:${code}`))
          .filter((area): area is TrackingAreaBoundary => area !== undefined)
      );
    };

    if (missing.length === 0) {
      paint();
      return undefined;
    }

    getTrackingAreaBoundaries(level, missing)
      .then((areas) => {
        areas.forEach((area) =>
          boundaryCache.current.set(`${level}:${area.code}`, area)
        );
        paint();
      })
      // The outline is decoration; the filter it belongs to still works, and
      // the backend simply omits areas core has no geometry for.
      .catch(() => paint());

    return () => {
      current = false;
    };
  }, [areaLevel, outlineCodes, showAreaOutlines]);

  const suggestions = useMemo(() => trackingSearchSuggestions(items), [items]);
  const chosenFilterCount = countChosenFilters(
    filters,
    sameRungs(rungs, DEFAULT_RUNGS),
    rungs.length
  );

  const filterBar = (
    <TrackingFilterBar
      rungs={rungs}
      onRungsChange={changeRungs}
      filters={filters}
      onFiltersChange={setFilters}
      onClear={clearFilters}
      options={filterOptions}
      suggestions={suggestions}
      chosenFilterCount={chosenFilterCount}
      visibleCount={visibleItems.length}
      totalCount={items.length}
    />
  );

  const emptyMessage = hasActiveTrackingFilters(filters)
    ? 'Tidak ada unit yang cocok dengan pencarian dan filter ini.'
    : 'Tidak ada unit di rung yang dipilih dan tidak ada case repossession terbuka.';

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="px-6 pt-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900">
              Tracking Unit Tunggakan
            </h1>
            <p className="text-[13px] text-gray-600">
              Posisi terakhir unit yang perlu dimatikan atau ditarik, dari
              telemetry OEM. Pilih unit untuk melihat riwayat pergerakannya.
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-3">
            <div className="flex items-center gap-2 text-[13px] text-gray-700">
              <Toggle
                checked={autoRefresh}
                onChange={setAutoRefresh}
                ariaLabel="Refresh otomatis"
              />
              <span>Auto-refresh</span>
              <SelectField
                value={String(intervalMinutes)}
                onChange={(value) => setIntervalMinutes(Number(value))}
                disabled={!autoRefresh}
                ariaLabel="Interval refresh"
                className="w-36"
                options={POLL_INTERVAL_OPTIONS.map((opt) => ({
                  value: String(opt.minutes),
                  label: `tiap ${opt.label}`,
                }))}
              />
            </div>
            <div className="flex items-center gap-2 text-[13px] text-gray-700">
              <Toggle
                checked={showAreaOutlines}
                onChange={setShowAreaOutlines}
                ariaLabel="Tampilkan batas wilayah"
              />
              <span title="Batas kabupaten atau kecamatan yang ditempati armada, mengikuti level di panel Sebaran area.">
                Batas wilayah
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-gray-700">
              <Toggle
                checked={showLabels}
                onChange={setShowLabels}
                ariaLabel="Tampilkan label unit"
              />
              <span title="Kalau mati, plat dan mitra hanya muncul saat kursor menyentuh titiknya.">
                Label unit
              </span>
            </div>
            <Button
              variant="secondary"
              size="small"
              onClick={() => load(true)}
              disabled={isRefreshing || isLoading}
            >
              <i
                className={`ri-refresh-line text-[12px] mr-1 ${
                  isRefreshing ? 'animate-spin' : ''
                }`}
              ></i>
              Refresh
            </Button>
          </div>
        </div>

        <Tabs
          items={TABS}
          active={activeTab}
          onChange={setActiveTab}
          ariaLabel="Tracking sections"
        />

        <TrackingSummaryBar summary={summary} asOf={asOf} className="py-2" />
      </div>

      {activeTab === 'map' ? (
        <div className="relative min-h-[420px] flex-1">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-[13px] text-gray-500">
              Memuat unit…
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-[13px] text-gray-500">
                  Memuat peta…
                </div>
              }
            >
              <TrackingMap
                items={visibleItems}
                selectedID={selectedID}
                onSelect={select}
                trail={trail}
                cursorMs={cursorMs}
                areaBoundaries={areaBoundaries}
                boundsPadding={MAP_PANEL_PADDING}
                highlightedAreaCodes={selectedAreaCodes}
                showLabels={showLabels}
                className="h-full w-full"
              />
            </Suspense>
          )}

          {/* Bottom-left, opposite the panel: what the dot colours mean, and
              the control that separates the three jobs from one another. */}
          <div className="pointer-events-none absolute bottom-3 left-3 z-10 hidden md:block">
            <div className="pointer-events-auto">
              <TrackingMapLegend
                tallies={rungTallies}
                selected={filters.rungs}
                onToggle={toggleRung}
                onClear={clearRungFilter}
              />
            </div>
          </div>

          {/* Floating over the map: the filter bar, then the selected unit.
              pointer-events are handed back per card so the map stays
              draggable in the gaps between them. */}
          <div className="pointer-events-none absolute inset-3 z-10 flex flex-col gap-2 md:inset-y-4 md:left-auto md:right-4 md:w-[420px]">
            <div className="pointer-events-auto relative z-20 flex-shrink-0 rounded-xl border border-gray-200 bg-white/95 p-2 shadow-lg backdrop-blur">
              {filterBar}
            </div>
            {selected ? (
              <div className="pointer-events-auto relative z-10 min-h-0 flex-1">
                <UnitTrailPanel
                  item={selected}
                  trail={trail}
                  isLoadingTrail={isLoadingTrail}
                  onLoadTrail={loadTrail}
                  onClearTrail={clearTrail}
                  cursorMs={cursorMs}
                  onCursorChange={setCursorMs}
                  onClose={() => select(null)}
                />
              </div>
            ) : (
              <>
                <div className="pointer-events-auto relative z-10 hidden flex-shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white/95 px-3 py-2 text-[12px] text-gray-500 shadow-lg backdrop-blur md:flex">
                  <i className="ri-cursor-line"></i>
                  Klik titik di peta untuk detail unit, atau area di bawah untuk
                  menyaring peta.
                </div>
                <div className="pointer-events-auto relative z-10 hidden min-h-0 flex-1 md:block">
                  <AreaBreakdownPanel
                    groups={areaGroups}
                    level={areaLevel}
                    onLevelChange={setAreaLevel}
                    selectedCodes={selectedAreaCodes}
                    onToggle={toggleArea}
                    onSelectUnit={select}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6">
          {/* Same control as on the map, so the table can be cut down to one
              job too — the colours here match the pills in the Status column. */}
          <div className="mb-3 flex flex-wrap items-start gap-3">
            <div className="min-w-[280px] flex-1 max-w-[720px]">{filterBar}</div>
            <TrackingMapLegend
              tallies={rungTallies}
              selected={filters.rungs}
              onToggle={toggleRung}
              onClear={clearRungFilter}
            />
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <TableData
              data={visibleItems}
              columns={columns}
              loading={isLoading}
              size="small"
              onRowClick={(record) => {
                select(record.rentalID);
                setActiveTab('map');
              }}
              emptyState={
                <div className="py-10 text-center text-[13px] text-gray-500">
                  {emptyMessage}
                </div>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RentTrackingPage;
